import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SpeechToTask from './SpeechToTask';
import DeleteTaskButton from './components/DeleteTaskButton';
import DeleteBoardButton from './components/DeleteBoardButton';
import UserAssignment from './components/UserAssignment';
import { TASK_COLORS, getRandomColorKey } from './TaskManager';
import * as taskService from './services/taskService';

// Define animations first
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(10px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const modalAppear = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const pulse = keyframes`
  0% {
    background-color: rgba(0, 122, 255, 0.03);
  }
  100% {
    background-color: rgba(0, 122, 255, 0.08);
  }
`;

const BoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #F5F5F7;  /* Apple light background */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #1D1D1F;  /* Apple dark text */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const BoardHeader = styled.header`
  background-color: rgba(255, 255, 255, 0.8);  /* Semi-transparent white */
  backdrop-filter: blur(10px);  /* Apple-style blur */
  -webkit-backdrop-filter: blur(10px);
  color: #1D1D1F;
  padding: 1.25rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);  /* Subtle shadow */
  position: sticky;
  top: 0;
  z-index: 10;
`;

const BoardTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;  /* Apple-style tight letter spacing */
`;

const BackButton = styled.button`
  background-color: transparent;
  color: #007AFF;  /* Apple blue */
  border: 1px solid rgba(0, 122, 255, 0.3);
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: rgba(0, 122, 255, 0.05);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const BoardContent = styled.main`
  flex: 1;
  padding: 2rem;
  overflow: auto;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const ColumnsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  height: 100%;
  overflow-x: auto;
  padding-bottom: 1rem;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const Column = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  width: 320px;
  min-width: 320px;
  height: fit-content;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.03);
  transition: transform 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const ColumnHeader = styled.div`
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const ColumnTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1D1D1F;
  letter-spacing: -0.01em;
`;

const TaskCount = styled.span`
  background-color: rgba(0, 0, 0, 0.05);
  color: #1D1D1F;
  border-radius: 12px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const TaskList = styled.div`
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
  min-height: 100px;
  transition: background-color 0.2s ease;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
  
  /* Add a subtle animation when a column is being dragged over */
  ${props => props.isDraggingOver && css`
    animation: ${pulse} 1.5s infinite alternate;
  `}
`;

const TaskCard = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 0.8rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.02);
  animation: ${slideIn} 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: ${props => props.color ? TASK_COLORS[props.color] || TASK_COLORS.gray : TASK_COLORS.gray};
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.08);
  }
`;

const TaskContent = styled.div`
  flex: 1;
`;

const TaskActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  transform: translateY(5px);
  
  ${TaskCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TaskCardInner = styled.div`
  display: flex;
  flex-direction: column;
`;

const TaskText = styled.p`
  margin: 0;
  color: #1D1D1F;
  padding-left: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const TaskDescription = styled.p`
  margin: 0.5rem 0 0 0;
  color: #86868B;
  padding-left: 0.5rem;
  font-size: 0.8rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const TaskDate = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #86868B;
  padding-left: 0.5rem;
`;

const AddTaskButton = styled.button`
  background-color: transparent;
  border: none;
  color: #007AFF;
  padding: 0.75rem;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: 0 0 10px 10px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  
  &:hover {
    background-color: rgba(0, 122, 255, 0.05);
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  width: 450px;
  max-width: 90vw;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  animation: ${modalAppear} 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
  color: #1D1D1F;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #1D1D1F;
  font-size: 0.875rem;
  letter-spacing: -0.01em;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15);
  }
  
  &::placeholder {
    color: #86868B;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15);
  }
  
  &::placeholder {
    color: #86868B;
  }
`;

const ColorOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ColorOption = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 2px solid ${props => props.selected ? '#007AFF' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  &:focus {
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  gap: 1rem;
`;

const Button = styled.button`
  background-color: ${props => props.variant === 'outline' ? 'transparent' : props.color || '#007AFF'};
  color: ${props => props.variant === 'outline' ? (props.color || '#007AFF') : 'white'};
  border: ${props => props.variant === 'outline' ? `1px solid ${props.color || '#007AFF'}` : 'none'};
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: ${props => props.variant === 'outline' 
      ? 'rgba(0, 122, 255, 0.05)' 
      : (props.color ? props.colorHover || props.color : '#0062CC')};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SpeechSection = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.03);
  max-width: 600px;
`;

const ToggleSpeechButton = styled(Button)`
  margin-bottom: 1rem;
`;

const ErrorBanner = styled.div`
  background-color: rgba(255, 59, 48, 0.08);
  border: 1px solid rgba(255, 59, 48, 0.2);
  border-left: 4px solid #FF3B30;
  color: #1D1D1F;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${slideIn} 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 5V8.5" stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 11.5V12" stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DismissButton = styled.button`
  background: none;
  border: none;
  color: #86868B;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
  
  &:hover {
    color: #1D1D1F;
  }
`;

// SVG Icons
const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const BoardView = ({ board, onBack, onUpdateBoard }) => {
  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [selectedColor, setSelectedColor] = useState('blue');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskColumn, setNewTaskColumn] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [assignedUserId, setAssignedUserId] = useState(null);
  const [error, setError] = useState(null);

  // Get the board ID (either id for localStorage or _id for MongoDB)
  const boardId = board.id || board._id;
  
  // Log board information for debugging
  useEffect(() => {
    console.log("Board data:", board);
    console.log("Board ID used:", boardId);
    console.log("Board ID type:", typeof boardId);
  }, [board, boardId]);

  // Function to ensure MongoDB ObjectID compatibility
  const getValidMongoId = (id) => {
    if (!id) return null;
    
    // MongoDB ObjectIDs are 24-character hexadecimal strings
    // If the ID is exactly 24 characters and hexadecimal, use it as is
    if (/^[0-9a-f]{24}$/i.test(id)) {
      console.log(`ID ${id} is already a valid MongoDB ObjectID`);
      return id;
    }
    
    // If the ID has a leading underscore, remove it and check again
    if (id.startsWith('_') && /^_[0-9a-f]{24}$/i.test(id)) {
      const cleanId = id.substring(1);
      console.log(`Cleaned ID ${id} to ${cleanId}`);
      return cleanId;
    }
    
    // If we have a valid 24-character hex ID embedded somewhere in the string
    const hexMatch = id.match(/([0-9a-f]{24})/i);
    if (hexMatch) {
      console.log(`Extracted embedded MongoDB ObjectID ${hexMatch[1]} from ${id}`);
      return hexMatch[1];
    }
    
    // If none of the above, return the original ID and hope for the best
    console.warn(`Could not extract a valid MongoDB ObjectID from ${id}`);
    return id;
  };
  
  // Function to fetch tasks for the current board
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get tasks for this board
      console.log(`Attempting to fetch tasks for board: ${boardId}`);
      
      try {
        // First ensure the board's columns are properly initialized
        const boardColumns = board.columns || [];
        
        // Create a deep copy of the columns to avoid modifying the original board
        // Initialize columns even if we don't have tasks yet
        const columnsWithEmptyTasks = boardColumns.map(column => ({
          ...column,
          tasks: [] // Start with empty tasks for each column
        }));
        
        // Set the columns structure immediately to avoid UI flashing
        setColumns(columnsWithEmptyTasks);
        
        // Try to get board with tasks using the enhanced API endpoint
        try {
          console.log('Fetching board with tasks using enhanced endpoint');
          const enhancedBoard = await taskService.getBoard(boardId);
          
          // Check if we received tasks with the board
          if (enhancedBoard && enhancedBoard.tasks && Array.isArray(enhancedBoard.tasks) && enhancedBoard.tasks.length > 0) {
            console.log(`Using ${enhancedBoard.tasks.length} tasks from enhanced board endpoint`);
            
            // Normalize tasks to ensure they have consistent id fields
            const normalizedTasks = enhancedBoard.tasks.map(task => ({
              ...task,
              id: task.id || task._id, // Ensure task.id exists
              _id: task._id || task.id, // Ensure task._id exists
              text: task.title || task.text || '' // Ensure task.text exists for backward compatibility
            }));
            
            // Set the tasks in state
            setTasks(normalizedTasks);
            
            // Update the columns with the tasks
            const columnsWithTasks = enhancedBoard.columns.map(column => {
              // Get tasks for this column
              const columnTasks = normalizedTasks.filter(task => task.columnId === column.id) || [];
              
              // If the column already has tasks from the API, use those
              if (column.tasks && Array.isArray(column.tasks) && column.tasks.length > 0) {
                // Normalize the existing tasks
                const normalizedColumnTasks = column.tasks.map(task => ({
                  ...task,
                  id: task.id || task._id,
                  _id: task._id || task.id,
                  text: task.title || task.text || ''
                }));
                return {
                  ...column,
                  tasks: normalizedColumnTasks
                };
              } else {
                // Otherwise use the filtered tasks
                return {
                  ...column,
                  tasks: columnTasks
                };
              }
            });
            
            setColumns(columnsWithTasks);
            return; // We're done if we successfully got tasks from the enhanced endpoint
          }
        } catch (enhancedError) {
          console.warn('Enhanced board endpoint failed, falling back to standard methods:', enhancedError);
          // Continue with standard task fetching methods
        }
        
        // Check if the board already has tasks array embedded (from the API response)
        let tasksArray = [];
        
        if (board.tasks && Array.isArray(board.tasks) && board.tasks.length > 0) {
          console.log(`Using ${board.tasks.length} tasks directly from board data`);
          tasksArray = board.tasks;
        } else {
          // If not, fetch the tasks separately
          console.log('No tasks found in board data, fetching separately');
          const tasksData = await taskService.getTasks(boardId);
          // Defensive programming - ensure tasksData is an array
          tasksArray = Array.isArray(tasksData) ? tasksData : [];
        }
        
        // Normalize tasks to ensure they have consistent id fields
        const normalizedTasks = tasksArray.map(task => ({
          ...task,
          id: task.id || task._id, // Ensure task.id exists
          _id: task._id || task.id, // Ensure task._id exists
          text: task.title || task.text || '' // Ensure task.text exists for backward compatibility
        }));
        
        // Set the tasks in state
        setTasks(normalizedTasks);
        console.log(`Successfully set ${normalizedTasks.length} tasks in state`);
        
        // Now update the columns with the tasks
        const columnsWithTasks = boardColumns.map(column => ({
          ...column,
          // Map tasks that belong to this column
          tasks: normalizedTasks.filter(task => {
            return task.columnId === column.id;
          }) || []
        }));
        
        setColumns(columnsWithTasks);
      } catch (taskError) {
        console.error('Error fetching tasks, continuing with empty task list:', taskError);
        // Continue with empty tasks rather than failing completely
        setTasks([]);
        
        // Still set up columns but with empty tasks
        const boardColumns = board.columns || [];
        const columnsWithEmptyTasks = boardColumns.map(column => ({
          ...column,
          tasks: []
        }));
        setColumns(columnsWithEmptyTasks);
        
        // Set a non-blocking error message
        setError('Unable to load tasks. The board will be displayed with empty columns.');
      }
    } catch (err) {
      console.error('Error setting up board view:', err);
      setError('Failed to load board view. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [boardId, board.columns, board.tasks]);

  // Load tasks when the board changes
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    
    // If dropped outside a droppable area
    if (!destination) return;
    
    // If dropped in the same position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) return;
    
    try {
      // Find the task being moved
      const taskId = draggableId;
      const task = tasks.find(t => (t.id === taskId || t._id === taskId));
      
      if (!task) {
        console.error(`Task not found with id: ${taskId}`);
        return;
      }
      
      // Create a copy of the current columns state
      const newColumns = [...columns];
      
      // If moving to a different column
      if (source.droppableId !== destination.droppableId) {
        console.log(`Moving task ${taskId} from ${source.droppableId} to ${destination.droppableId}`);
        
        // Get source and destination columns
        const sourceColumn = newColumns.find(col => col.id === source.droppableId);
        const destColumn = newColumns.find(col => col.id === destination.droppableId);
        
        if (!sourceColumn || !destColumn) {
          console.error('Source or destination column not found');
          return;
        }
        
        // Move to new column using the PATCH endpoint
        try {
          // Get a valid MongoDB ID for the task
          const validTaskId = getValidMongoId(task._id || task.id);
          
          if (!validTaskId) {
            throw new Error("Could not determine a valid task ID for movement");
          }
          
          console.log(`Moving task with ID: ${validTaskId} to column: ${destination.droppableId}`);
          await taskService.moveTask(validTaskId, destination.droppableId);
          
          // Update local task state
          setTasks(prevTasks => prevTasks.map(t => 
            (t.id === taskId || t._id === taskId) 
              ? { ...t, columnId: destination.droppableId } 
              : t
          ));
          
          // Update columns state
          // 1. Remove from source column
          const updatedSourceTasks = sourceColumn.tasks.filter(
            t => t.id !== taskId && t._id !== taskId
          );
          
          // 2. Add to destination column
          const movedTask = { ...task, columnId: destination.droppableId };
          const updatedDestTasks = [...destColumn.tasks, movedTask];
          
          // 3. Update the columns state
          setColumns(prevColumns => {
            return prevColumns.map(column => {
              if (column.id === source.droppableId) {
                return { ...column, tasks: updatedSourceTasks };
              }
              if (column.id === destination.droppableId) {
                return { ...column, tasks: updatedDestTasks };
              }
              return column;
            });
          });
          
          // Update taskIds in both columns to reflect the new order
          const updatedBoard = { ...board };
          const sourceColumnInBoard = updatedBoard.columns.find(c => c.id === source.droppableId);
          const destColumnInBoard = updatedBoard.columns.find(c => c.id === destination.droppableId);
          
          if (sourceColumnInBoard && sourceColumnInBoard.taskIds) {
            sourceColumnInBoard.taskIds = sourceColumnInBoard.taskIds.filter(id => id !== taskId);
          }
          
          if (destColumnInBoard && destColumnInBoard.taskIds) {
            destColumnInBoard.taskIds = [...(destColumnInBoard.taskIds || []), taskId];
          }
          
          // Update the board to save the new task arrangement
          onUpdateBoard(updatedBoard);
        } catch (moveError) {
          console.error('Error moving task:', moveError);
          setError('Failed to move task. Please try again.');
          
          // If the move fails, revert the UI back to the original state
          fetchTasks();
        }
      } else {
        // Same column reordering - just update the taskIds order in the board
        const column = newColumns.find(col => col.id === source.droppableId);
        if (column && column.tasks) {
          // Reorder tasks array
          const reorderedTasks = [...column.tasks];
          const [removed] = reorderedTasks.splice(source.index, 1);
          reorderedTasks.splice(destination.index, 0, removed);
          
          // Update column tasks
          setColumns(prevColumns => 
            prevColumns.map(col => 
              col.id === source.droppableId 
                ? { ...col, tasks: reorderedTasks } 
                : col
            )
          );
          
          // Update taskIds in the board to reflect the new order
          const updatedBoard = { ...board };
          const columnInBoard = updatedBoard.columns.find(c => c.id === source.droppableId);
          
          if (columnInBoard && columnInBoard.taskIds) {
            const reorderedIds = [...columnInBoard.taskIds];
            const [removedId] = reorderedIds.splice(source.index, 1);
            reorderedIds.splice(destination.index, 0, removedId);
            columnInBoard.taskIds = reorderedIds;
            
            // Update the board
            onUpdateBoard(updatedBoard);
          }
        }
      }
    } catch (err) {
      console.error('Error in drag and drop:', err);
      setError('Failed to update task position. Please try again.');
    }
  };

  const handleAddTask = async () => {
    console.log('handleAddTask called - button clicked');
    if (!newTaskTitle.trim() || !newTaskColumn) return;
    
    console.log('Passed validation checks - proceeding with task operation');
    try {
      // Get a valid MongoDB ObjectID format
      const mongoDbBoardId = getValidMongoId(boardId);
      
      if (!mongoDbBoardId) {
        throw new Error("Could not determine a valid board ID for this task");
      }
      
      // Check if we're editing an existing task
      if (currentTask) {
        console.log('Save button clicked - editing existing task:', currentTask);
        // Get the task ID
        const taskId = currentTask.id || currentTask._id;
        console.log('Task ID for update:', taskId);
        
        // Update the task with new values
        const updatedTask = {
          ...currentTask,
          title: newTaskTitle.trim(),
          description: newTaskDescription.trim(),
          color: selectedColor,
          assignedUserId: assignedUserId
        };
        
        console.log('Calling updateTask with data:', updatedTask);
        // Update task in API
        await taskService.updateTask(taskId, updatedTask);
        console.log('updateTask API call completed successfully');
        
        // Update local state
        setTasks(prevTasks => 
          prevTasks.map(task => 
            (task.id === taskId || task._id === taskId) 
              ? {...task, title: newTaskTitle.trim(), description: newTaskDescription.trim(), color: selectedColor, assignedUserId: assignedUserId} 
              : task
          )
        );
        
        // Update columns state
        setColumns(prevColumns => 
          prevColumns.map(column => ({
            ...column,
            tasks: column.tasks.map(task => 
              (task.id === taskId || task._id === taskId) 
                ? {...task, title: newTaskTitle.trim(), description: newTaskDescription.trim(), color: selectedColor, assignedUserId: assignedUserId} 
                : task
            )
          }))
        );
        
        // Reset form and close modal
        setCurrentTask(null);
        setNewTaskTitle('');
        setNewTaskDescription('');
        setSelectedColor('blue');
        setAssignedUserId(null);
        setIsTaskModalOpen(false);
        console.log('Task update completed, modal closed');
        return;
      }
      
      // Create a new task with minimal required fields according to the API guide
      const newTask = {
        title: newTaskTitle.trim(),
        description: newTaskDescription.trim(),
        boardId: mongoDbBoardId,
        columnId: newTaskColumn,
        color: selectedColor,
        assignedUserId: assignedUserId
      };
      
      console.log('Attempting to create task:', newTask);
      
      // Save the task
      const createdTask = await taskService.createTask(newTask);
      
      // Update local tasks state - ensure we have a valid task object
      if (createdTask && (createdTask.id || createdTask._id)) {
        console.log('Task created successfully:', createdTask);
        
        // Check if we received an updated board with the task creation response
        if (createdTask._updatedBoard) {
          console.log('Received updated board with task creation response:', createdTask._updatedBoard);
          
          // Use the updated board data from the enhanced API
          const updatedBoardData = createdTask._updatedBoard;
          
          // Normalize the updated board to ensure it has all required properties
          const normalizedBoard = {
            ...updatedBoardData,
            id: updatedBoardData._id || updatedBoardData.id,
            _id: updatedBoardData._id || updatedBoardData.id
          };
          
          // Process columns to ensure they have proper structure with tasks array
          if (normalizedBoard.columns && Array.isArray(normalizedBoard.columns)) {
            normalizedBoard.columns = normalizedBoard.columns.map(column => {
              // Extract task metadata if available
              const columnTasks = [];
              
              // Check if we have taskMetadata in the column (from the enhanced API)
              if (column.taskMetadata && Array.isArray(column.taskMetadata)) {
                // Convert task metadata to full tasks
                columnTasks.push(...column.taskMetadata.map(metadata => ({
                  id: metadata.id || metadata._id,
                  _id: metadata._id || metadata.id,
                  title: metadata.title,
                  text: metadata.title, // Maintain compatibility with old code
                  description: metadata.description || '',
                  color: metadata.color || 'blue',
                  columnId: column.id,
                  boardId: mongoDbBoardId
                })));
              }
              
              return {
                ...column,
                id: column.id,
                taskIds: Array.isArray(column.taskIds) ? column.taskIds : [],
                tasks: columnTasks
              };
            });
          }
          
          // Update board state with the new data
          onUpdateBoard(normalizedBoard);
          
          // Extract all tasks from columns to update tasks state
          const allTasks = [];
          normalizedBoard.columns.forEach(column => {
            if (Array.isArray(column.tasks)) {
              allTasks.push(...column.tasks);
            }
          });
          
          // Update tasks state
          setTasks(allTasks);
          
          // Update columns state
          setColumns(normalizedBoard.columns);
        } else {
          // Fall back to the old way of updating if we don't have updated board data
          
          // Add to local tasks state
          setTasks(prevTasks => [...prevTasks, createdTask]);
          
          // Update columns state
          setColumns(prevColumns => {
            return prevColumns.map(column => {
              if (column.id === newTaskColumn) {
                return {
                  ...column,
                  tasks: [...column.tasks, createdTask]
                };
              }
              return column;
            });
          });
          
          // Update the board's taskIds for this column
          const updatedBoard = { ...board };
          const columnToUpdate = updatedBoard.columns.find(c => c.id === newTaskColumn);
          
          if (columnToUpdate) {
            // Ensure taskIds is an array
            if (!columnToUpdate.taskIds) columnToUpdate.taskIds = [];
            
            // Add the new task ID to the column's taskIds
            const taskId = createdTask.id || createdTask._id;
            columnToUpdate.taskIds.push(taskId);
            
            // Update the board
            onUpdateBoard(updatedBoard);
            
            // Fetch the latest board data using the enhanced endpoint
            try {
              console.log('Fetching updated board data after task creation');
              const updatedBoardData = await taskService.getBoard(mongoDbBoardId);
              
              if (updatedBoardData && updatedBoardData.columns) {
                // Update the board with the latest data
                onUpdateBoard(updatedBoardData);
                
                // If the updated board has tasks, update our tasks state
                if (updatedBoardData.tasks && Array.isArray(updatedBoardData.tasks)) {
                  setTasks(updatedBoardData.tasks);
                  
                  // Update columns with the new tasks
                  const updatedColumns = updatedBoardData.columns.map(column => ({
                    ...column,
                    tasks: updatedBoardData.tasks.filter(task => task.columnId === column.id) || []
                  }));
                  
                  setColumns(updatedColumns);
                }
              }
            } catch (fetchError) {
              console.error('Error fetching updated board data:', fetchError);
              // We already updated local state, so this isn't critical
            }
          }
        }
      } else {
        console.error('Created task is invalid:', createdTask);
        setError('Task was created but returned an invalid response. Please refresh the board.');
      }
      
      // Reset form
      setNewTaskTitle('');
      setNewTaskDescription('');
      setSelectedColor('blue');
      setAssignedUserId(null);
      setIsTaskModalOpen(false);
    } catch (err) {
      console.error('Error adding task:', err);
      setError(`Failed to add task: ${err.message || 'Unknown error'}`);
    }
  };

  // Handle editing a task
  const handleEditTask = (columnId, taskId) => {
    const column = columns.find(col => col.id === columnId);
    if (column) {
      // Find the task using either id or _id
      const task = column.tasks.find(t => 
        (t.id && t.id === taskId) || (t._id && t._id === taskId)
      );
      
      if (task) {
        console.log('Editing task:', task);
        setCurrentTask(task);
        // Use title or text for the title input field
        setNewTaskTitle(task.title || task.text || '');
        // Use description for the description field
        setNewTaskDescription(task.description || '');
        setSelectedColor(task.color || 'blue');
        setAssignedUserId(task.assignedUserId || null);
        // Set the column ID so the save operation doesn't get blocked
        setNewTaskColumn(columnId);
        setIsTaskModalOpen(true);
      }
    }
  };
  
  // Extract color intent from the speech transcript
  const extractColorFromText = (text) => {
    const colorRegex = /\b(blue|red|green|yellow|purple|teal|orange|pink|gray)\b/i;
    const match = text.match(colorRegex);
    return match ? match[0].toLowerCase() : getRandomColorKey();
  };
  
  // Function to handle adding tasks from speech
  const handleAddTaskFromSpeech = (taskText, transcript) => {
    return new Promise((resolve, reject) => {
      if (!board) {
        const error = new Error("No board is selected. Cannot add task.");
        setError(error.message);
        reject(error);
        return;
      }
      
      if (!taskText || taskText.trim() === '') {
        const error = new Error("Empty task text. Please try again with clearer speech.");
        setError(error.message);
        reject(error);
        return;
      }
      
      // Extract color from transcript if present
      const colorKey = extractColorFromText(transcript);
      
      // Default to adding tasks to the "To Do" column
      const todoColumnId = 'todo';
      
      try {
        // Get a valid MongoDB ObjectID format
        const mongoDbBoardId = getValidMongoId(boardId);
        
        if (!mongoDbBoardId) {
          const error = new Error("Could not determine a valid board ID for this task");
          setError(error.message);
          reject(error);
          return;
        }
        
        // Create a new task with required fields per API guide
        const newTask = {
          title: taskText, // Use the speech text as the title
          description: `Created via speech input on ${new Date().toLocaleDateString()}`,
          boardId: mongoDbBoardId,
          columnId: todoColumnId,
          color: colorKey
        };
        
        console.log('Attempting to create speech task:', newTask);
        
        // Add task to API
        taskService.createTask(newTask)
          .then(createdTask => {
            // Validate the created task
            if (!createdTask || (!createdTask.id && !createdTask._id)) {
              const error = new Error('Task was created but returned an invalid response.');
              console.error('Invalid task created:', createdTask);
              setError(error.message);
              reject(error);
              return;
            }
            
            console.log('Speech task created successfully:', createdTask);
            
            // Check if we received an updated board with the task creation response
            if (createdTask._updatedBoard) {
              console.log('Received updated board with task creation response:', createdTask._updatedBoard);
              
              // Use the updated board data from the enhanced API
              const updatedBoardData = createdTask._updatedBoard;
              
              // Normalize the updated board to ensure it has all required properties
              const normalizedBoard = {
                ...updatedBoardData,
                id: updatedBoardData._id || updatedBoardData.id,
                _id: updatedBoardData._id || updatedBoardData.id
              };
              
              // Process columns to ensure they have proper structure with tasks array
              if (normalizedBoard.columns && Array.isArray(normalizedBoard.columns)) {
                normalizedBoard.columns = normalizedBoard.columns.map(column => {
                  // Extract task metadata if available
                  const columnTasks = [];
                  
                  // Check if we have taskMetadata in the column (from the enhanced API)
                  if (column.taskMetadata && Array.isArray(column.taskMetadata)) {
                    // Convert task metadata to full tasks
                    columnTasks.push(...column.taskMetadata.map(metadata => ({
                      id: metadata.id || metadata._id,
                      _id: metadata._id || metadata.id,
                      title: metadata.title,
                      text: metadata.title, // Maintain compatibility with old code
                      description: metadata.description || '',
                      color: metadata.color || 'blue',
                      columnId: column.id,
                      boardId: mongoDbBoardId
                    })));
                  }
                  
                  return {
                    ...column,
                    id: column.id,
                    taskIds: Array.isArray(column.taskIds) ? column.taskIds : [],
                    tasks: columnTasks
                  };
                });
              }
              
              // Update board state with the new data
              onUpdateBoard(normalizedBoard);
              
              // Extract all tasks from columns to update tasks state
              const allTasks = [];
              normalizedBoard.columns.forEach(column => {
                if (Array.isArray(column.tasks)) {
                  allTasks.push(...column.tasks);
                }
              });
              
              // Update tasks state
              setTasks(allTasks);
              
              // Update columns state
              setColumns(normalizedBoard.columns);
              
              // Resolve the promise as everything is successful
              resolve(createdTask);
            } else {
              // Fall back to the old way of updating if we don't have updated board data
              
              // Update local state
              setTasks(prevTasks => [...prevTasks, createdTask]);
              
              // Update columns state
              setColumns(prevColumns => {
                return prevColumns.map(column => {
                  if (column.id === todoColumnId) {
                    return {
                      ...column,
                      tasks: [...column.tasks, createdTask]
                    };
                  }
                  return column;
                });
              });
              
              // Update the board's taskIds for this column
              const updatedBoard = { ...board };
              const columnToUpdate = updatedBoard.columns.find(c => c.id === todoColumnId);
              
              if (columnToUpdate) {
                // Ensure taskIds is an array
                if (!columnToUpdate.taskIds) columnToUpdate.taskIds = [];
                
                // Add the new task ID to the column's taskIds
                const taskId = createdTask.id || createdTask._id;
                columnToUpdate.taskIds.push(taskId);
                
                // Update the board
                onUpdateBoard(updatedBoard);
                
                // Fetch the latest board data using the enhanced endpoint
                taskService.getBoard(mongoDbBoardId)
                  .then(updatedBoardData => {
                    if (updatedBoardData && updatedBoardData.columns) {
                      // Update the board with the latest data
                      onUpdateBoard(updatedBoardData);
                      
                      // If the updated board has tasks, update our tasks state
                      if (updatedBoardData.tasks && Array.isArray(updatedBoardData.tasks)) {
                        setTasks(updatedBoardData.tasks);
                        
                        // Update columns with the new tasks
                        const updatedColumns = updatedBoardData.columns.map(column => ({
                          ...column,
                          tasks: updatedBoardData.tasks.filter(task => task.columnId === column.id) || []
                        }));
                        
                        setColumns(updatedColumns);
                      }
                    }
                    // Resolve the promise after updating
                    resolve(createdTask);
                  })
                  .catch(fetchError => {
                    console.error('Error fetching updated board data:', fetchError);
                    // We already updated local state, so this isn't critical
                    // Still resolve since the task was created
                    resolve(createdTask);
                  });
              } else {
                // No column found, but task was created, so resolve
                resolve(createdTask);
              }
            }
          })
          .catch(err => {
            console.error("Error adding task:", err);
            setError('Failed to add task from speech. Please try again.');
            reject(err);
          });
      } catch (err) {
        console.error("Error preparing task:", err);
        setError('Failed to prepare task. Please try again.');
        reject(err);
      }
    });
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Handle task deletion
  const handleTaskDeleted = (columnId, taskId) => {
    // Update tasks state
    setTasks(prevTasks => prevTasks.filter(t => t.id !== taskId && t._id !== taskId));
    
    // Update columns state
    setColumns(prevColumns => 
      prevColumns.map(column => ({
        ...column,
        tasks: column.tasks.filter(t => t.id !== taskId && t._id !== taskId)
      }))
    );
    
    // Update the board's taskIds
    const updatedBoard = { ...board };
    updatedBoard.columns = updatedBoard.columns.map(column => {
      if (column.taskIds && Array.isArray(column.taskIds)) {
        column.taskIds = column.taskIds.filter(id => id !== taskId);
      }
      return column;
    });
    
    // Update the board
    onUpdateBoard(updatedBoard);
  };

  // Handle board deletion
  const handleBoardDelete = () => {
    // Call the onBack function to return to dashboard
    onBack();
  };

  return (
    <BoardContainer>
      <BoardHeader>
        <BoardTitle>{board.title || 'Untitled Board'}</BoardTitle>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <DeleteBoardButton 
            boardId={board.id || board._id}
            boardTitle={board.title || 'Untitled Board'}
            onSuccess={handleBoardDelete}
          />
          <BackButton onClick={onBack}>
            <BackIcon />
            Back to Dashboards
          </BackButton>
        </div>
      </BoardHeader>
      
      <BoardContent>
        {error && (
          <ErrorBanner>
            <ErrorMessage>
              <ErrorIcon />
              {error}
            </ErrorMessage>
            <DismissButton onClick={() => setError(null)}>
              Dismiss
            </DismissButton>
          </ErrorBanner>
        )}

        <ToggleSpeechButton 
          onClick={() => setIsAddingTask(!isAddingTask)}
        >
          {isAddingTask ? 'Hide Voice Input' : 'Add Task by Voice'}
        </ToggleSpeechButton>
        
        {isAddingTask && (
          <SpeechSection>
            <SpeechToTask onAddTask={handleAddTaskFromSpeech} />
          </SpeechSection>
        )}
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <ColumnsContainer>
            {columns.map(column => (
              <Column key={column.id}>
                <ColumnHeader>
                  <ColumnTitle>{column.title}</ColumnTitle>
                  <TaskCount>{column.tasks.length}</TaskCount>
                </ColumnHeader>
                
                <Droppable droppableId={column.id}>
                  {(provided, snapshot) => (
                    <TaskList
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      isDraggingOver={snapshot.isDraggingOver}
                      style={{
                        background: snapshot.isDraggingOver 
                          ? 'rgba(0, 122, 255, 0.05)'  /* Light blue when dragging over */
                          : 'transparent',
                        transition: 'background-color 0.2s ease, transform 0.2s ease',
                        borderRadius: '8px',
                      }}
                    >
                      {column.tasks.map((task, index) => (
                        <Draggable 
                          key={task.id || task._id} 
                          draggableId={task.id || task._id} 
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TaskCard
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => handleEditTask(column.id, task.id || task._id)}
                              color={task.color || 'gray'}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? '0.9' : '1',
                                transform: snapshot.isDragging 
                                  ? `${provided.draggableProps.style.transform} scale(1.02)`
                                  : provided.draggableProps.style.transform,
                                boxShadow: snapshot.isDragging 
                                  ? '0 10px 20px rgba(0, 0, 0, 0.12)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04)',
                              }}
                            >
                              <TaskCardInner>
                                <TaskText>{task.title || task.text}</TaskText>
                                {task.description && <TaskDescription>{task.description}</TaskDescription>}
                                <TaskDate>
                                  {new Date(task.createdAt || Date.now()).toLocaleDateString()}
                                </TaskDate>
                                <TaskActions>
                                  <DeleteTaskButton 
                                    taskId={task.id || task._id} 
                                    taskTitle={task.title || task.text}
                                    onSuccess={(deletedTaskId) => handleTaskDeleted(column.id, deletedTaskId)}
                                  />
                                </TaskActions>
                              </TaskCardInner>
                            </TaskCard>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TaskList>
                  )}
                </Droppable>
                
                <AddTaskButton onClick={() => {
                  setNewTaskColumn(column.id);
                  setNewTaskTitle('');
                  setNewTaskDescription('');
                  setSelectedColor('blue');
                  setIsTaskModalOpen(true);
                }}>
                  <PlusIcon />
                  Add a card
                </AddTaskButton>
              </Column>
            ))}
          </ColumnsContainer>
        </DragDropContext>
      </BoardContent>
      
      {isTaskModalOpen && (
        <Modal>
          <ModalContent>
            <ModalTitle>{currentTask ? 'Edit Task' : 'Add Task'}</ModalTitle>
            
            <FormGroup>
              <Label htmlFor="taskTitle">Task Title</Label>
              <Input 
                id="taskTitle"
                placeholder="Enter task title..." 
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                autoFocus
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="taskDescription">Task Description</Label>
              <TextArea 
                id="taskDescription"
                placeholder="Enter task description..." 
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                style={{ minHeight: '80px' }}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Card Color</Label>
              <ColorOptions>
                {Object.entries(TASK_COLORS).map(([colorKey, colorValue]) => (
                  <ColorOption 
                    key={colorKey}
                    color={colorValue}
                    selected={selectedColor === colorKey}
                    onClick={() => setSelectedColor(colorKey)}
                    aria-label={`Select ${colorKey} color`}
                  />
                ))}
              </ColorOptions>
            </FormGroup>
            
            <UserAssignment
              assignedUserId={assignedUserId}
              onAssign={setAssignedUserId}
            />
            
            <ButtonGroup>
              <Button 
                variant="outline"
                onClick={() => {
                  setIsTaskModalOpen(false);
                  setNewTaskTitle('');
                  setNewTaskDescription('');
                  setSelectedColor('blue');
                  setAssignedUserId(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddTask}>
                {currentTask ? 'Save' : 'Add'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </BoardContainer>
  );
};

export default BoardView; 